import styled from 'foundations-theming-specialty/utils';
import { Popover } from './atoms/Popover';
import { FormLabel } from 'foundations-components/transitional/components/FormLabel';
import { Text } from 'foundations-components/components/Text';
import { Flex } from 'foundations-components/components/Flex';
import { ToggleGroup } from 'foundations-components/transitional/components/ToggleGroup';
import { RadioInput } from 'foundations-components/transitional/components/RadioInput';
import { Select } from 'foundations-components/transitional/components/Select';
import { Button } from 'foundations-components/transitional/components/Button';
import { useHasCompanyPermissions } from '../hooks/useHasCompanyPermissions';
import { useMemo, useState } from 'react';
import { DEFAULT_FILTER_VALUES, generateStatusFilterOptions, generateTicketViewFilterOptions } from '../constants/filters';
import { generateDateFilterOptions } from '../constants/filters';
import { useHasConfiguredColumn } from '../utils/useHasConfiguredColumn';
import { useConfiguredColumnLabelOverride } from '../utils/useConfiguredColumnLabelOverride';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { AdvancedFiltersIcon } from 'foundations-assets/icons/components/AdvancedFiltersIcon';
import I18n from 'I18n';
import { useIsMobile } from '../hooks/useBreakpoint';
import { MobilePopover } from './MobilePopover';
import { usageTracker } from '../utils/usageTracker';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const FilterPopoverGrid = styled.div.withConfig({
  displayName: "TicketListingsFilterPopover__FilterPopoverGrid"
})(["display:grid;grid-template-columns:auto 1fr;gap:", ";"], ({
  theme
}) => theme.space[600]);
const FilterLabel = ({
  label,
  htmlFor
}) => {
  return /*#__PURE__*/_jsx(FormLabel, {
    htmlFor: htmlFor,
    children: /*#__PURE__*/_jsx(Text, {
      whiteSpace: "nowrap",
      typography: "body-100",
      color: "text-core-subtle",
      children: label
    })
  });
};
const FilterPopoverContent = ({
  statusFilter,
  setStatusFilter,
  ticketView,
  setTicketView,
  createDateFilter,
  setCreateDateFilter,
  lastActivityFilter,
  setLastActivityFilter,
  resetFilters
}) => {
  const isMobile = useIsMobile();
  const hasCompanyPermissions = useHasCompanyPermissions();
  const ticketViewOptions = useMemo(() => generateTicketViewFilterOptions(), []);
  const createdateFilterOptions = useMemo(() => generateDateFilterOptions(), []);
  const lastTicketActivityFilterOptions = useMemo(() => generateDateFilterOptions(), []);
  const hasCreatedateColumn = useHasConfiguredColumn('createdate');
  const hasLastTicketActivityColumn = useHasConfiguredColumn('hs_lastactivitydate');
  const createdateFilterLabel = useConfiguredColumnLabelOverride('createdate');
  const lastTicketActivityFilterLabel = useConfiguredColumnLabelOverride('hs_lastactivitydate');
  const statusFilterOptions = useMemo(() => generateStatusFilterOptions(), []);
  const statusFilterLabel = useConfiguredColumnLabelOverride('status');
  return /*#__PURE__*/_jsxs(Flex, {
    maxInlineSize: isMobile ? '100%' : '650px',
    inlineSize: isMobile ? '100%' : '500px',
    flexDirection: "column",
    gap: "600",
    padding: "1000",
    children: [!isMobile && /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      justifyContent: "space-between",
      children: [/*#__PURE__*/_jsx(Text, {
        typography: "heading-100",
        color: "text-core-default",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customer-portal-app.filterLabel"
        })
      }), /*#__PURE__*/_jsx(Button, {
        use: "transparent",
        size: "small",
        onClick: () => {
          usageTracker.track('Customer Portal App', 'Tickets Discovered', {
            action: 'tickets-filter-cleared',
            screen: 'ticket-listings'
          });
          resetFilters();
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customer-portal-app.filters.resetFilters"
        })
      })]
    }), /*#__PURE__*/_jsxs(FilterPopoverGrid, {
      children: [/*#__PURE__*/_jsx(FilterLabel, {
        htmlFor: "statusFilter",
        label: statusFilterLabel
      }), /*#__PURE__*/_jsx(ToggleGroup, {
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        gap: "400",
        rowGap: "0",
        name: "statusFilter",
        id: "statusFilter",
        "data-test-id": "status-filter",
        children: statusFilterOptions.map(option => /*#__PURE__*/_jsx("div", {
          children: /*#__PURE__*/_jsx(RadioInput, {
            value: option.value,
            "aria-label": option.text,
            inline: true,
            checked: statusFilter === option.value,
            onChange: () => {
              usageTracker.track('Customer Portal App', 'Tickets Discovered', {
                action: 'tickets-filtered',
                screen: 'ticket-listings',
                status: option.value
              });
              setStatusFilter(option.value);
            },
            children: /*#__PURE__*/_jsx(Text, {
              color: "text-core-default",
              children: option.text
            })
          })
        }, option.value))
      }), hasCompanyPermissions && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(FilterLabel, {
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customer-portal-app.filters.ticketView"
          })
        }), /*#__PURE__*/_jsx(ToggleGroup, {
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          gap: "400",
          rowGap: "0",
          name: "ticketView",
          id: "ticketView",
          "data-test-id": "ticket-view-filter",
          children: ticketViewOptions.map(option => /*#__PURE__*/_jsx("div", {
            children: /*#__PURE__*/_jsx(RadioInput, {
              value: option.value,
              "aria-label": option.text,
              inline: true,
              checked: ticketView === option.value,
              onChange: () => {
                usageTracker.track('Customer Portal App', 'Tickets Discovered', {
                  action: 'ticket-ownership-view-toggled',
                  screen: 'ticket-listings',
                  view: option.value
                });
                setTicketView(option.value);
              },
              children: /*#__PURE__*/_jsx(Text, {
                color: "text-core-default",
                children: option.text
              })
            })
          }, option.value))
        })]
      }), hasCreatedateColumn && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(FilterLabel, {
          htmlFor: "createdateFilter",
          label: createdateFilterLabel
        }), /*#__PURE__*/_jsx(Select, {
          maxInlineSize: "250px",
          id: "createdateFilter",
          "data-test-id": "createdate-filter",
          searchable: false,
          value: createDateFilter,
          onChange: e => {
            usageTracker.track('Customer Portal App', 'Tickets Discovered', {
              action: 'tickets-filtered',
              screen: 'ticket-listings',
              timeRange: e.target.value
            });
            setCreateDateFilter(e.target.value);
          },
          options: createdateFilterOptions
        })]
      }), hasLastTicketActivityColumn && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(FilterLabel, {
          htmlFor: "lastTicketActivityFilter",
          label: lastTicketActivityFilterLabel
        }), /*#__PURE__*/_jsx(Select, {
          maxInlineSize: "250px",
          id: "lastTicketActivityFilter",
          "data-test-id": "last-activity-filter",
          searchable: false,
          value: lastActivityFilter,
          onChange: e => setLastActivityFilter(e.target.value),
          options: lastTicketActivityFilterOptions
        })]
      })]
    })]
  });
};
const FilterPopoverButton = ({
  filterCount,
  open,
  setOpen
}) => /*#__PURE__*/_jsxs(Button, {
  use: "transparent",
  "data-test-id": "filter-button",
  onClick: () => {
    if (!open) {
      usageTracker.track('Customer Portal App', 'Tickets Discovered', {
        action: 'ticket-filter-discovered',
        screen: 'ticket-listings'
      });
    }
    setOpen(!open);
  },
  children: [/*#__PURE__*/_jsx(AdvancedFiltersIcon, {}), /*#__PURE__*/_jsxs("span", {
    children: [/*#__PURE__*/_jsx(FormattedMessage, {
      message: "customer-portal-app.filterLabel"
    }), filterCount > 0 && ` (${filterCount})`]
  })]
});
export const FilterPopover = props => {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();
  const {
    statusFilter,
    createDateFilter,
    lastActivityFilter
  } = props;
  const filterCount = useMemo(() => {
    let count = 0;
    if (statusFilter !== DEFAULT_FILTER_VALUES.statusFilter) {
      count++;
    }
    if (createDateFilter !== DEFAULT_FILTER_VALUES.createDateFilter) {
      count++;
    }
    if (lastActivityFilter !== DEFAULT_FILTER_VALUES.lastActivityFilter) {
      count++;
    }
    return count;
  }, [statusFilter, createDateFilter, lastActivityFilter]);
  if (isMobile) {
    return /*#__PURE__*/_jsx(MobilePopover, {
      title: I18n.text('customer-portal-app.filterLabel'),
      open: open,
      onOpenChange: setOpen,
      content: /*#__PURE__*/_jsx(FilterPopoverContent, Object.assign({}, props)),
      footer: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        justifyContent: "start",
        gap: "400",
        children: [/*#__PURE__*/_jsx("div", {
          children: /*#__PURE__*/_jsx(Button, {
            use: "primary",
            onClick: () => setOpen(false),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "customer-portal-app.show"
            })
          })
        }), /*#__PURE__*/_jsx("div", {
          children: /*#__PURE__*/_jsx(Button, {
            use: "transparent",
            onClick: () => {
              usageTracker.track('Customer Portal App', 'Tickets Discovered', {
                action: 'tickets-filter-cleared',
                screen: 'ticket-listings'
              });
              props.resetFilters();
            },
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "customer-portal-app.filters.resetFilters"
            })
          })
        })]
      }),
      children: /*#__PURE__*/_jsx(FilterPopoverButton, {
        filterCount: filterCount,
        open: open,
        setOpen: setOpen
      })
    });
  }
  return /*#__PURE__*/_jsx(Popover, {
    arrowSize: "none",
    marginInlineEnd: "1000",
    open: open,
    content: /*#__PURE__*/_jsx(FilterPopoverContent, Object.assign({}, props)),
    onOpenChange: e => setOpen(e.currentTarget.value),
    children: /*#__PURE__*/_jsx(FilterPopoverButton, {
      filterCount: filterCount,
      open: open,
      setOpen: setOpen
    })
  });
};